import { AssignV3Strategy } from "@redotech/redo-flows-common/rules/actions";
import { AssignmentTypeOption } from "@redotech/redo-flows-common/rules/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { Schema } from "effect";
import { makeBind } from "../field-binding";
import { DefaultRule } from "../types";

/**
 * Shape of this rule's `configurations` JSONB blob — mirrors
 * `AssignV3ActionConfig` from the rules model, minus the fixed `actionType`.
 * Matches the input a merchant supplies when editing a normal ASSIGN_V3
 * action.
 */
const AssignV3ModeSchema = Schema.Union(
  Schema.Struct({
    _tag: Schema.Literal("specific_users"),
    mongoUserIds: Schema.Array(Schema.String),
  }),
  Schema.Struct({
    _tag: Schema.Literal("user_group"),
    userGroupId: Schema.String,
  }),
  Schema.Struct({ _tag: Schema.Literal("unassign") }),
);

export const AssignOnConversationCreatedConfigSchema = Schema.Struct({
  mode: AssignV3ModeSchema,
  strategy: Schema.Enums(AssignV3Strategy),
  maxTicketsPerUser: Schema.optional(Schema.NullOr(Schema.Number)),
  activeUsersOnly: Schema.optional(Schema.Boolean),
});

export type AssignOnConversationCreatedConfig =
  typeof AssignOnConversationCreatedConfigSchema.Type;

const bind = makeBind<AssignOnConversationCreatedConfig>();

/**
 * Default rule: Assign on new customer message
 *
 * When a new customer message is received and the conversation is currently
 * unassigned, run the standard ASSIGN_V3 action using merchant-configured
 * parameters (mode, strategy, max tickets per user). Starts disabled so
 * merchants explicitly configure the assignment targets before it takes effect.
 */
export const assignOnConversationCreatedRule = {
  id: "default-assign-on-conversation-created",
  name: "Assign on new customer message",
  description:
    "When a new customer message is received and the conversation is unassigned, auto-assign it using the configured assignment settings.",
  hidden: false,
  triggerType: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,

  configSchema: AssignOnConversationCreatedConfigSchema,

  defaultConfig: {
    defaultRuleId: "default-assign-on-conversation-created",
    // Starts disabled — the default mode has no users so the rule has
    // nothing useful to do until a merchant configures it.
    enabled: false,
    sortIndex: 2,
    configurations: {
      mode: { _tag: "specific_users", mongoUserIds: [] },
      strategy: AssignV3Strategy.ROUND_ROBIN,
      maxTicketsPerUser: null,
      activeUsersOnly: false,
    },
  },

  template: {
    name: "Assign on new customer message",
    description:
      "When a new customer message is received and the conversation is unassigned, auto-assign it using the configured assignment settings.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,
        nextId: "conditions",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: [AssignmentTypeOption.NO_ASSIGNEE],
              },
              queryOperator: QueryOperator.IS,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [
          {
            actionType: RuleActionType.ASSIGN_V3,
            mode: bind("mode", { _tag: "specific_users", mongoUserIds: [] }),
            strategy: bind("strategy", AssignV3Strategy.ROUND_ROBIN),
            maxTicketsPerUser: bind("maxTicketsPerUser", null),
            activeUsersOnly: bind("activeUsersOnly", false),
          },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule<string, AssignOnConversationCreatedConfig>;
